'use client';

/**
 * Consent state for a child organisation (GitLab #649).
 *
 * Owns the whole lifecycle: read the stored choice, decide whether the gate
 * must block, persist a new choice, tell Google about it, clean up on
 * withdrawal and reload when a tag needs to start.
 *
 * Renders no UI. The gate and preferences panel (Phase 4) consume this via
 * `useConsent()`, which keeps the compliance logic testable without a DOM
 * and stops it being duplicated across the surfaces that need it.
 */
import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { actionForCategories, allCategoriesGranted, clearOptionalCookies, essentialOnlyCategories, hasOptionalConsent, isConsentValid, normaliseCategories, readOrMintAnonymousId, readStoredConsent, writeStoredConsent, type ConsentAction, type ConsentCategories, type StoredConsent } from '@/lib/analytics/consentCookie';
import { pushConsentEvent, pushConsentUpdate } from '@/lib/analytics/consentMode';
import type { AnalyticsSettings } from '@/types/organization';

/** Passed to `onChoice` so Phase 6 can write the server-side evidence row. */
export interface ConsentChoiceRecord {
  organizationId?: string;
  anonymousId: string;
  categories: ConsentCategories;
  consentVersion: number;
  action: ConsentAction;
}
export interface ConsentContextValue {
  /** False until the stored cookie has been read — nothing should render before this. */
  isReady: boolean;
  /** Whether this org has analytics configured and switched on at all. */
  isEnabled: boolean;
  /** True when the blocking gate must be shown. */
  isGateOpen: boolean;
  /** True when the preferences panel is expanded. */
  isPreferencesOpen: boolean;
  /** The current valid choice, or `null` if none has been made. */
  consent: StoredConsent | null;
  categories: ConsentCategories;
  /** Whether GTM should be injected — enabled, configured and optional consent given. */
  shouldLoadGtm: boolean;
  acceptAll: () => void;
  acceptEssentialOnly: () => void;
  savePreferences: (categories: Partial<ConsentCategories>) => void;
  withdrawConsent: () => void;
  openPreferences: () => void;
  closePreferences: () => void;
}
const ConsentContext = createContext<ConsentContextValue | null>(null);
export interface ConsentProviderProps {
  children: ReactNode;
  settings: AnalyticsSettings;
  /** Child org the consent is given under; recorded on the evidence row. */
  organizationId?: string;
  /**
   * Suppresses the gate without disabling consent handling — used for paths
   * where a reload would break a journey, such as the open-banking callback.
   */
  suppressGate?: boolean;
  /** Hook for the Phase 6 server-side consent log. */
  onChoice?: (record: ConsentChoiceRecord) => void;
  /** Test seam: jsdom does not implement navigation. */
  reload?: () => void;
}
export function ConsentProvider(t0) {
  const $ = _c(45);
  const {
    children,
    settings,
    organizationId,
    suppressGate: t1,
    onChoice,
    reload
  } = t0;
  const suppressGate = t1 === undefined ? false : t1;
  const isEnabled = settings.enabled && !!settings.gtm_container_id;
  const [isReady, setIsReady] = useState(!isEnabled);
  const [consent, setConsent] = useState(null);
  const [isGateOpen, setIsGateOpen] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const consentRef = useRef(null);
  let t2;
  let t3;
  if ($[0] !== consent) {
    t2 = () => {
      consentRef.current = consent;
    };
    t3 = [consent];
    $[0] = consent;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== isEnabled || $[4] !== settings || $[5] !== suppressGate) {
    t4 = () => {
      if (!isEnabled) {
        return;
      }
      const stored = readStoredConsent();
      if (stored && isConsentValid(stored, settings)) {
        setConsent(stored);
        pushConsentUpdate(stored.categories);
        setIsGateOpen(false);
      } else {
        setConsent(null);
        setIsGateOpen(!suppressGate);
      }
      setIsReady(true);
    };
    t5 = [isEnabled, settings, suppressGate];
    $[3] = isEnabled;
    $[4] = settings;
    $[5] = suppressGate;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  useEffect(t4, t5);
  let t6;
  if ($[8] !== onChoice || $[9] !== organizationId || $[10] !== reload || $[11] !== settings.consent_lifetime_days || $[12] !== settings.consent_version) {
    t6 = next => {
      const previous = consentRef.current;
      const categories = normaliseCategories(next);
      const anonymousId = previous?.anonymous_id ?? readOrMintAnonymousId(settings.consent_lifetime_days);
      const record = {
        version: settings.consent_version,
        timestamp: new Date().toISOString(),
        anonymous_id: anonymousId,
        categories
      };
      writeStoredConsent(record, settings.consent_lifetime_days);
      consentRef.current = record;
      setConsent(record);
      setIsGateOpen(false);
      setIsPreferencesOpen(false);
      const hadOptional = previous ? hasOptionalConsent(previous.categories) : false;
      const hasOptional = hasOptionalConsent(categories);
      const isWithdrawal = hadOptional && !hasOptional;
      if (isWithdrawal) {
        clearOptionalCookies();
      }
      pushConsentUpdate(categories);
      pushConsentEvent(isWithdrawal ? "consent_withdrawn" : previous ? "consent_updated" : "consent_granted", categories);
      onChoice?.({
        organizationId,
        anonymousId,
        categories,
        consentVersion: settings.consent_version,
        action: isWithdrawal ? "withdrawn" : actionForCategories(categories, !!previous)
      });
      if (!hadOptional && hasOptional || isWithdrawal) {
        (reload ?? _temp)();
      }
    };
    $[8] = onChoice;
    $[9] = organizationId;
    $[10] = reload;
    $[11] = settings.consent_lifetime_days;
    $[12] = settings.consent_version;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const persist = t6;
  let t7;
  if ($[14] !== persist || $[15] !== settings.categories) {
    t7 = () => {
      const offered = settings.categories;
      if (offered.length === 0) {
        return persist(allCategoriesGranted());
      }
      const next_0 = essentialOnlyCategories();
      for (const config of offered) {
        next_0[config.category] = true;
      }
      return persist(next_0);
    };
    $[14] = persist;
    $[15] = settings.categories;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const acceptAll = t7;
  let t8;
  if ($[17] !== persist) {
    t8 = () => persist(essentialOnlyCategories());
    $[17] = persist;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const acceptEssentialOnly = t8;
  let t9;
  if ($[19] !== persist) {
    t9 = partial => persist(normaliseCategories({
      ...essentialOnlyCategories(),
      ...partial
    }));
    $[19] = persist;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const savePreferences = t9;
  let t10;
  if ($[21] !== persist) {
    t10 = () => persist(essentialOnlyCategories());
    $[21] = persist;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const withdrawConsent = t10;
  let t11;
  if ($[23] !== consent?.categories) {
    t11 = consent?.categories ?? essentialOnlyCategories();
    $[23] = consent?.categories;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const categories_0 = t11;
  const t12 = isGateOpen && !suppressGate;
  let t13;
  if ($[25] !== categories_0 || $[26] !== isEnabled) {
    t13 = isEnabled && hasOptionalConsent(categories_0);
    $[25] = categories_0;
    $[26] = isEnabled;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setIsPreferencesOpen(true);
    t15 = () => setIsPreferencesOpen(false);
    $[28] = t14;
    $[29] = t15;
  } else {
    t14 = $[28];
    t15 = $[29];
  }
  let t16;
  if ($[30] !== acceptAll || $[31] !== acceptEssentialOnly || $[32] !== categories_0 || $[33] !== consent || $[34] !== isEnabled || $[35] !== isPreferencesOpen || $[36] !== isReady || $[37] !== savePreferences || $[38] !== t12 || $[39] !== t13 || $[40] !== withdrawConsent) {
    t16 = {
      isReady,
      isEnabled,
      isGateOpen: t12,
      isPreferencesOpen,
      consent,
      categories: categories_0,
      shouldLoadGtm: t13,
      acceptAll,
      acceptEssentialOnly,
      savePreferences,
      withdrawConsent,
      openPreferences: t14,
      closePreferences: t15
    };
    $[30] = acceptAll;
    $[31] = acceptEssentialOnly;
    $[32] = categories_0;
    $[33] = consent;
    $[34] = isEnabled;
    $[35] = isPreferencesOpen;
    $[36] = isReady;
    $[37] = savePreferences;
    $[38] = t12;
    $[39] = t13;
    $[40] = withdrawConsent;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const value = t16;
  let t17;
  if ($[42] !== children || $[43] !== value) {
    t17 = <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
    $[42] = children;
    $[43] = value;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  return t17;
}

/**
 * Consent state. Throws outside a provider so a miswired surface fails
 * loudly in development rather than silently rendering no gate.
 */
function _temp() {
  return window.location.reload();
}
export function useConsent() {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error("useConsent must be used within a ConsentProvider");
  }
  return context;
}

/**
 * Consent state where a provider may legitimately be absent — the shared
 * footer renders on admin surfaces that have no consent gate, and its
 * "Cookie settings" link should simply not appear there.
 */
export function useConsentSafe() {
  return useContext(ConsentContext);
}